import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { MessageCircle, Linkedin, Mail, Phone, Send, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { SECTION, CONTACT } from "@/constants/testIds";
import { useI18n } from "@/i18n/I18nContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP_URL = "https://wa.me/33672328408";
const LINKEDIN_URL = "https://linkedin.com/in/diallobirrou";
const EMAIL = "diallobirrou@gmail.com";

export default function ContactSection() {
  const { t } = useI18n();
  const c = t.contact;
  const [form, setForm] = useState({
    name: "",
    email: "",
    organization: "",
    subject: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/contact`, form);
      toast.success(res.data?.message || c.toasts.success);
      setSent(true);
      setForm({ name: "", email: "", organization: "", subject: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail || c.toasts.error;
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid={SECTION.contact}
      className="relative section-y bg-navy text-white overflow-hidden"
    >
      <div className="absolute inset-0 -z-0">
        <div className="absolute inset-0 grid-bg opacity-25" />
        <div className="absolute -top-32 right-0 h-[480px] w-[480px] rounded-full bg-cyan-tech/10 blur-3xl" />
        <div className="absolute bottom-0 left-0 h-[380px] w-[380px] rounded-full bg-amber-500/10 blur-3xl" />
      </div>

      <div className="container-x relative grid lg:grid-cols-12 gap-10 lg:gap-16">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-5"
        >
          <span className="text-[11px] uppercase tracking-[0.22em] text-cyan-tech font-semibold">
            {c.overline}
          </span>
          <h2 className="mt-4 font-display font-semibold text-3xl sm:text-4xl lg:text-5xl leading-[1.05]">
            {c.title}
          </h2>
          <p className="mt-6 text-slate-300 leading-relaxed">{c.paragraph}</p>

          <div className="mt-10 space-y-3">
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noreferrer noopener"
              data-testid={CONTACT.whatsapp}
              className="flex items-center gap-3 p-4 rounded-2xl bg-white/5 border border-white/10 hover:border-cyan-tech transition-colors group"
            >
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-cyan-tech text-navy">
                <MessageCircle className="h-5 w-5" />
              </span>
              <div>
                <div className="font-medium">{c.whatsappTitle}</div>
                <div className="text-sm text-slate-400">+33 6 72 32 84 08</div>
              </div>
            </a>

            <a
              href={LINKEDIN_URL}
              target="_blank"
              rel="noreferrer noopener"
              data-testid={CONTACT.linkedin}
              className="flex items-center gap-3 p-4 rounded-2xl bg-white/5 border border-white/10 hover:border-cyan-tech transition-colors"
            >
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-white/10 text-white">
                <Linkedin className="h-5 w-5" />
              </span>
              <div>
                <div className="font-medium">{c.linkedinTitle}</div>
                <div className="text-sm text-slate-400">linkedin.com/in/diallobirrou</div>
              </div>
            </a>

            <a
              href={`mailto:${EMAIL}`}
              className="flex items-center gap-3 p-4 rounded-2xl bg-white/5 border border-white/10 hover:border-cyan-tech transition-colors"
            >
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-white/10 text-white">
                <Mail className="h-5 w-5" />
              </span>
              <div>
                <div className="font-medium">{c.emailTitle}</div>
                <div className="text-sm text-slate-400">{EMAIL}</div>
              </div>
            </a>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="lg:col-span-7"
        >
          <div className="rounded-3xl bg-white/95 text-slate-900 p-7 lg:p-10 backdrop-blur border border-white/20 shadow-2xl">
            {sent ? (
              <div className="text-center py-10" data-testid="contact-success">
                <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto" />
                <h3 className="mt-4 font-display text-2xl font-semibold text-navy">
                  {c.success.title}
                </h3>
                <p className="mt-2 text-slate-600">{c.success.desc}</p>
                <button
                  onClick={() => setSent(false)}
                  className="mt-6 btn-outline"
                  data-testid="contact-send-another"
                >
                  {c.success.again}
                </button>
              </div>
            ) : (
              <form data-testid={CONTACT.form} onSubmit={onSubmit} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <Field
                    label={c.form.name}
                    required
                    value={form.name}
                    onChange={onChange("name")}
                    testid={CONTACT.inputName}
                    placeholder={c.form.namePh}
                  />
                  <Field
                    label={c.form.email}
                    required
                    type="email"
                    value={form.email}
                    onChange={onChange("email")}
                    testid={CONTACT.inputEmail}
                    placeholder={c.form.emailPh}
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <Field
                    label={c.form.organization}
                    value={form.organization}
                    onChange={onChange("organization")}
                    testid={CONTACT.inputOrg}
                    placeholder={c.form.organizationPh}
                  />
                  <Field
                    label={c.form.subject}
                    required
                    value={form.subject}
                    onChange={onChange("subject")}
                    testid={CONTACT.inputSubject}
                    placeholder={c.form.subjectPh}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-800 mb-1.5">
                    {c.form.message} <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    required
                    minLength={10}
                    rows={5}
                    value={form.message}
                    onChange={onChange("message")}
                    data-testid={CONTACT.inputMessage}
                    placeholder={c.form.messagePh}
                    className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-navy/30 focus:border-navy resize-none"
                  />
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
                  <div className="text-xs text-slate-500 flex items-center gap-2">
                    <Phone className="h-3.5 w-3.5" /> {c.form.replyDelay}
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid={CONTACT.submit}
                    className="btn-primary disabled:opacity-60"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" /> {c.form.sending}
                      </>
                    ) : (
                      <>
                        {c.form.submit} <Send className="h-4 w-4" />
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function Field({ label, required, type = "text", value, onChange, testid, placeholder }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-800 mb-1.5">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        type={type}
        required={required}
        value={value}
        onChange={onChange}
        data-testid={testid}
        placeholder={placeholder}
        className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-navy/30 focus:border-navy"
      />
    </div>
  );
}
